/**
 * **同一个字的不同出现位置之间互相跳转** —— 导入预览与「浏览与编辑」**共用这一份实现**。
 *
 * ## 用户原话（验收标准）
 *
 * > "目前在录入汉字时，粘贴并点预览，如果同一个字出现两次以上……我找到一个字并改了它的
 * >  读音，之后**想找它另外一个的时候，就要不断地往下翻**。字数比较多时比较难找。
 * >  有没有办法：当我点其中一个多音字并给它注了音之后，在**弹出的浮窗里面加一个功能，
 * >  直接定位到另外一个字**，让我能跳转过去。也就是说，对于任何一个配对或者多音字，
 * >  都有**跳转到另外一个字**的功能。"
 *
 * > "在**「浏览」模块**下面也一样：……**同一个界面中出现多音字，也能互相跳转**，
 * >  因为多音字不是相邻的，可能位于一页不同的位置。"
 *
 * ## 为什么只写一份
 *
 * 两处要的行为逐字相同（滚到那张卡 → 短暂高亮 → 焦点落到卡上），
 * 而本项目已经吃过"两处各写一份、改一处忘一处"的亏（见 `HanziCard.tsx` 的文件头）。
 * 所以：
 *
 *   · **纯函数 + 定时器注入**的 `runHanziJump()` —— 滚动 / 高亮 / 聚焦 / 取消全在这里，
 *     不需要 DOM 就能测（`hanziJump.test.ts`）；
 *   · `useHanziJump()` —— 只负责 React 那一层：登记字卡 DOM、维护"正在高亮哪一个"、
 *     等目标卡渲染出来再滚；
 *   · 两个浮窗里那块「这个字还出现在」是同一个 `HanziJumpLinks` 组件。
 *
 * ## ⚠️ 2026-10-20：跳转**不打开浮窗**（用户点名去掉了那个"省一次点击"）
 *
 * 用户原话：
 *
 * > "跳转过后呢，**你不要自动点击那个跳转的字**，然后你一点的话，它就自动弹出那个
 * >  浮窗来了。就是你跳过去之后呢，你**高亮 2 秒**嘛，对不对？那你就**高亮 2 秒就行了，
 * >  你不要把它的弹窗点开**呀。……**两边都是一样**。"
 *
 * 所以一次跳转**只做三件事**，做完就结束：
 *   1. `scrollIntoView({ block: 'center', behavior: 'smooth' })` 滚到目标卡；
 *   2. `focus({ preventScroll: true })` 把焦点落到那张卡上（键盘 / 读屏友好）；
 *   3. 高亮 `HANZI_JUMP_HIGHLIGHT_MS`（2 秒）后自动消失。
 *
 * ⛔ 这里**没有**"到位回调"这种东西，也**不许**再加回来：调用方
 *    （`ImportListingPreview` / `DictationWorkspace`）跳完之后唯一该做的就是什么都不做
 *    —— 目标位置的浮窗由用户**自己点那张卡**才打开（`hanziJump.test.ts` 有源码守卫）。
 *
 * ## 几个刻意的地方（都是用户点名过的）
 *
 *   · **滚动**：手机上 `center` 才不会把卡停在屏幕最底下、被键盘 / 浮窗挡住；
 *   · **reduced-motion**：`prefers-reduced-motion: reduce` ⇒ `behavior: 'auto'`
 *     （瞬时滚过去，不做动画）；
 *   · **高亮**：`activeKey` 变化触发字卡的**品牌蓝浅底脉冲**（`HanziCard` 的
 *     `jump-flash`），`HANZI_JUMP_HIGHLIGHT_MS`（2 秒）后自动清掉 —— **不会永久亮着**；
 *     离开页面时一并取消；
 *   · **焦点**：滚到位后 `focus({ preventScroll: true })` 落到目标卡（键盘 / 读屏友好）；
 *   · **滚动锁**：`Modal` 打开时会给 `body` 加 `overflow: hidden`。所以调用方的顺序是
 *     **先关浮窗、再跳**（见 `ImportListingPreview` / `DictationWorkspace`），本文件
 *     还会等一两帧确认 `body` 解锁后再滚 —— 不是"关窗时乱滚"，滚动只发生在用户
 *     点了"跳到另外一个字"之后。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ListingPositionInfo } from '@fht/shared';
import type { HanziView } from '../../lib/api/dictation';
import { listTypeShortLabel } from '../../lib/api/dictation';

// ---------------------------------------------------------------- 常量

/** 跳过去之后，目标卡的**底色脉冲**亮多久（毫秒）。2 秒，用户点名要的"短暂高亮"。 */
export const HANZI_JUMP_HIGHLIGHT_MS = 2000;

/**
 * 底色脉冲的节奏：**0.8 秒一个脉冲，共 2.5 次** ⇒ 正好 2 秒。
 *
 * ⚠️ 与 `HANZI_JUMP_HIGHLIGHT_MS` **必须对齐**（`hanziJump.test.ts` 有乘积断言）：
 *    动画最后一帧回到卡片本色，同一时刻 `useHanziJump` 把 `highlighted` 清掉，
 *    底色才会"干净地"消失、不残留。
 * ⚠️ 0.8 秒 = **每秒 1.25 次**，刻意慢：用户 2026-10-20 要求"以底色的形式**闪烁**"，
 *    但"必须是**柔和脉冲**，不是刺眼快闪"—— 也让光敏用户安全。
 *    ⛔ 别再调快到 < 0.6 秒（> 1.67 次/秒）或加 `alternate` / 无限循环。
 * 具体 CSS 在 `apps/web/tailwind.config.js` 的 `jump-flash`（keyframes + animation）。
 */
export const HANZI_JUMP_FLASH_DURATION_MS = 800;
export const HANZI_JUMP_FLASH_ITERATIONS = 2.5;

/**
 * 目标卡还没渲染出来时最多等几帧（≈ 40 帧 ≈ 0.6 秒）。
 *
 * 场景：换了筛选条件之后列表在**下一两帧**才提交完，卡片才注册进来；这里等一下
 * 而不是"点了没反应"。⛔ 不要再为了"分页展开"加大它 —— 「浏览与编辑」现在
 * 一次渲染全部条目（2026-10-20 用户要求），没有"还没渲染出来的第 101 张卡"了。
 */
export const HANZI_JUMP_MAX_WAIT_FRAMES = 40;

/** 浮窗刚关、`body` 还锁着滚动时，最多再等几帧（等 `Modal` 的 cleanup 跑完）。 */
export const HANZI_JUMP_MAX_LOCK_WAIT_FRAMES = 10;

// ---------------------------------------------------------------- 类型

/**
 * 浮窗里「这个字还出现在」的一项 —— 两个浮窗**共用**这个形状。
 *
 * `label` 是给人看的位置：
 *   · 导入预览：`第 7 行第 1 个字`（后端算好的那句话）；
 *   · 浏览与编辑：`第 12 个 · 识字表`（同一次筛选结果里的第几个 + 它在哪张表上）。
 */
export interface HanziJumpCard {
  /** 跳转键（与字卡的 React key 同口径：`hanziRowKey()` / `hanziPositionKey()`）。 */
  key: string;
  /** 人话位置（见上）。 */
  label: string;
  /** 这个位置标定的读音；空串 / 不传 = 没标。 */
  reading?: string;
  /** true = 这个位置**必须**标音却还没标（导入预览用；浏览里不传）。 */
  needsReading?: boolean;
}

// ---------------------------------------------------------------- 位置键 / 展示

/** 导入预览里一个位置的跳转键（与字卡 React key 逐字相同）。 */
export function hanziPositionKey(line: number, indexInLine: number): string {
  return `${line}-${indexInLine}`;
}

/**
 * 「浏览与编辑」里一张字卡的**基础**跳转键。
 *
 * ⚠️ 同一个字录了 N 次 = N 行（`id` 相同、`listingId` 不同），所以**优先 `listingId`**；
 *    没有收录行的手工字退回 `id`。
 *
 * ⚠️ 真正拿去登记 / 当 React key 的是 `hanziRowKey(item, rowIndex)`：它在"没有
 *    `listingId` 且有同字多行"时还会加上行下标，保证每一行唯一。本函数只保留给
 *    "当前行没有行上下文"时兜底（`browseJumpCards` 的 `currentKey` 默认值）。
 */
export function hanziCardKey(item: HanziView): string {
  return item.listingId ?? item.id;
}

/**
 * **一张字卡在列表里的跳转键**（= React key + `register()` 的键）。
 *
 * 与 `hanziCardKey()` 的唯一区别：**没有 `listingId` 时也要按"行"唯一**。
 *
 * 为什么不能只用 `hanziCardKey()`：同一个字录了 N 次时 `id` 相同，旧口径
 * `listingId ?? id` 会在没有收录行（老后端 / 手工字）时把 N 行折叠成**同一个 key**
 * —— `Map.set` 后注册的覆盖先注册的，点"跳到第 2 个"落到第 1 个（2026-10-21
 * 用户报的「多音字跳转总是自己跳到自己」）。
 *
 * 收录行存在（`listingId` 非空）时**仍然只用 `listingId`**：它是数据库主键，
 * 与"这一行是哪个位置"一一对应，也是导入预览那条路之外唯一稳定的身份。
 *
 * @param index 这一行在**当前列表结果**里的下标（0 起）—— 只用于没有
 *        `listingId` 的兜底，保证同字的 N 行两两不同。
 */
export function hanziRowKey(item: HanziView, index: number): string {
  const listingId = item.listingId ?? null;
  if (listingId !== null) return listingId;
  /**
   * 下标 0 仍用**旧键 `id`**：手工建的、只出现一次的字一个字节都不变
   * （`highlightedKey` / 既有测试 / 别的调用方都不受影响）。只有同字的
   * **第 2 行起**才加 `#下标`，让它们与第 1 行分开。
   */
  return index === 0 ? item.id : `${item.id}#${index}`;
}

/**
 * 这张卡所在字表的名字（`识字表` / `写字表` / `生字`）。
 *
 * 来源：`item.listings` 里 `id === item.listingId` 的那一条（列表接口会批量带上）；
 * 拿不到就写「生字」（手工建的、还没归到任何一张表上的字）。
 */
export function hanziCardListTypeLabel(item: HanziView): string {
  const listingId = item.listingId ?? null;
  if (listingId === null) return '生字';
  const listing = (item.listings ?? []).find((entry) => entry.id === listingId) ?? null;
  return listing === null ? '生字' : listTypeShortLabel(listing.listType);
}

/** 这一项"标没标音"那句话；空串 = 不显示（浏览页老数据不该冒出一堆"未标音"）。 */
export function describeHanziJumpStatus(card: HanziJumpCard): string {
  const reading = (card.reading ?? '').trim();
  if (reading !== '') return `已标 ${reading}`;
  return card.needsReading === true ? '未标音' : '';
}

/** 一整项的人话：`第 7 行第 1 个字 · 未标音`。 */
export function describeHanziJumpCard(card: HanziJumpCard): string {
  const status = describeHanziJumpStatus(card);
  return status === '' ? card.label : `${card.label} · ${status}`;
}

/**
 * 导入预览：同一个字在**别的**位置（不含当前这个；空数组 = 只出现这一处）。
 *
 * ⚠️ 从 `positions` 自己筛，而不是只信后端的 `readings.repeated`：点**只出现一次**的字
 *    也能开窗（统一入口），那时这里就是空数组，浮窗说"没有别的位置了"。
 * ⚠️ 每个位置的**标音状态**一起带上 —— 用户要靠它判断"还剩哪个没标"
 *    （`第 3 行 · 已标 cháng` / `第 7 行 · 未标音`）。
 */
export function importJumpCards(
  positions: readonly ListingPositionInfo[],
  current: ListingPositionInfo | null,
): HanziJumpCard[] {
  if (current === null) return [];
  const currentKey = hanziPositionKey(current.line, current.indexInLine);
  return positions
    .filter((position) => position.charText === current.charText)
    .filter((position) => hanziPositionKey(position.line, position.indexInLine) !== currentKey)
    .map((position) => ({
      key: hanziPositionKey(position.line, position.indexInLine),
      label: position.label,
      reading: position.reading,
      needsReading: position.needsReading,
    }));
}

/**
 * 浏览与编辑：同一个字在**当前这次筛选结果**里的**其它**位置。
 *
 * ⚠️ 只在同一屏内跳（用户明确要求）：`items` 就是当前筛选结果（不是整库、不跨册）；
 *    调用方传的是本次列表响应的全部行，不只是已经渲染出来的前 100 张 ——
 *    「第 N 个」说的就是筛选结果里的名次（1 开始）。
 * ⚠️ 字在结果里只出现一次 ⇒ 返回空数组 ⇒ 浮窗里那整块**不渲染**（不留空壳）。
 * ⚠️ **当前这一行按"行"排除**：`currentKey` 是打开浮窗那一刻记下的**那张卡自己的
 *    跳转键**（`hanziRowKey`）。不能只靠 `current.listingId` —— 浮窗里的
 *    「读音汇总」DTO **没有** `listingId`，同步刷新后 `current` 会被换成那份数据，
 *    当前行就不再被排除（「还出现在」里多出一个"自己"）。没有 `listingId` 的兜底行
 *    更糟：多行共用 `id`，登记表里后注册的覆盖先注册的，点哪一行都落到第一行。
 */
export function browseJumpCards(
  items: readonly HanziView[],
  current: HanziView,
  /**
   * 打开浮窗的那张卡**自己的键**（`hanziRowKey(item, index)`）。
   *
   * 不传 ⇒ 退回 `hanziCardKey(current)`（兼容旧调用方 / 单测）。
   */
  currentKey?: string | null,
): HanziJumpCard[] {
  const currentKeyResolved = currentKey ?? hanziCardKey(current);
  const cards: HanziJumpCard[] = [];
  items.forEach((item, index) => {
    if (item.charText !== current.charText) return;
    const key = hanziRowKey(item, index);
    if (key === currentKeyResolved || item === current) return;
    cards.push({
      key,
      label: `第 ${index + 1} 个 · ${hanziCardListTypeLabel(item)}`,
      reading: item.listingReading ?? '',
      needsReading: false,
    });
  });
  return cards;
}

// ---------------------------------------------------------------- reduced motion / 滚动

export interface MatchMediaHost {
  matchMedia?: ((query: string) => { matches: boolean }) | undefined;
}

/**
 * 用户是不是要求**减少动态效果**。
 *
 * ⚠️ 拿不到 `matchMedia`（老浏览器 / 服务端渲染 / 测试环境）时返回 `false`：
 *    那只会多一段平滑滚动，不会少任何功能。
 */
export function prefersReducedMotion(host?: MatchMediaHost): boolean {
  const target =
    host ?? (typeof window === 'undefined' ? undefined : (window as unknown as MatchMediaHost));
  if (target === undefined || typeof target.matchMedia !== 'function') return false;
  return target.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** reduced-motion ⇒ `auto`（瞬时滚过去）；否则 `smooth`。 */
export function hanziJumpScrollBehavior(reduceMotion: boolean): ScrollBehavior {
  return reduceMotion ? 'auto' : 'smooth';
}

/**
 * 滚动 / 聚焦 / 定时要用的最小接口 —— 注入进来，`runHanziJump()` 就能在没有 DOM 的
 * 测试里跑（本仓不引 jsdom / @testing-library，见 `apps/web/vitest.config.ts`）。
 */
export interface HanziJumpElement {
  scrollIntoView(options?: ScrollIntoViewOptions): void;
  focus(options?: FocusOptions): void;
}

export interface HanziJumpTimers {
  requestFrame(callback: () => void): number;
  cancelFrame(handle: number): void;
  setTimeout(callback: () => void, ms: number): number;
  clearTimeout(handle: number): void;
}

export interface HanziJumpRunInput {
  /** 目标字卡的 DOM。 */
  element: HanziJumpElement;
  /** 用户要求减少动态效果？ */
  reduceMotion: boolean;
  timers: HanziJumpTimers;
  /** 高亮结束（2 秒）—— 调用方据此把"正在高亮"清掉。 */
  onHighlightEnd?: (() => void) | undefined;
  highlightMs?: number;
}

export interface HanziJumpRun {
  /** 取消这一次跳转（下一次跳转 / 用户另点了别的卡 / 组件卸载）。 */
  cancel(): void;
}

/**
 * 执行一次跳转：**一帧之后**滚过去 + 聚焦，`highlightMs` 后回调 `onHighlightEnd`。
 *
 * ⚠️ 等一帧再滚：调用方通常在同一个事件里先关掉浮窗（`setState`），等 React
 *    提交完、`Modal` 的 cleanup 把 `body.overflow` 还原之后再滚，才不会"滚不动"。
 * ⚠️ **没有"到位回调"**：跳完就结束 —— 目标位置的浮窗由用户自己点那张卡才打开
 *    （2026-10-20 用户明确要求两处都别自动弹窗）。
 */
export function runHanziJump(input: HanziJumpRunInput): HanziJumpRun {
  const highlightMs = input.highlightMs ?? HANZI_JUMP_HIGHLIGHT_MS;
  let cancelled = false;
  let highlightTimer: number | null = null;

  const frame = input.timers.requestFrame(() => {
    if (cancelled) return;
    input.element.scrollIntoView({
      block: 'center',
      behavior: hanziJumpScrollBehavior(input.reduceMotion),
    });
    /**
     * ⚠️ `preventScroll: true`：滚动的**只有** `scrollIntoView` 这一条路径；
     *    聚焦再顺手滚一下，位置就会偏掉（用户要求"别乱飞"）。
     */
    input.element.focus({ preventScroll: true });
  });

  highlightTimer = input.timers.setTimeout(() => {
    highlightTimer = null;
    if (!cancelled) input.onHighlightEnd?.();
  }, highlightMs);

  return {
    cancel() {
      if (cancelled) return;
      cancelled = true;
      input.timers.cancelFrame(frame);
      if (highlightTimer !== null) input.timers.clearTimeout(highlightTimer);
      highlightTimer = null;
    },
  };
}

// ---------------------------------------------------------------- React 那一层

export interface HanziJumpApi {
  /** 把一张字卡登记进来（传给 `HanziCard` 的 `cardRef`；同一个 key 返回同一个回调）。 */
  register(key: string): (element: HTMLElement | null) => void;
  /**
   * 跳到某个 key（滚到那张卡 + 高亮 2 秒 + 焦点落到卡上）—— **到此为止**。
   *
   * ⛔ 没有"跳完再做点什么"的回调：目标卡还没渲染出来时会等最多
   *    `HANZI_JUMP_MAX_WAIT_FRAMES` 帧，仍然没有就放弃（不会替用户打开浮窗）。
   */
  jumpTo(key: string): void;
  /** 取消正在进行的跳转 / 高亮（用户另点了别的卡、关浮窗、换筛选）。 */
  cancel(): void;
  /** 正在短暂高亮的卡（给 `HanziCard` 的 `highlighted`）；`null` = 没有。 */
  activeKey: string | null;
}

/** 浏览器里的定时器（rAF 不存在时退回 `setTimeout(0)`）。 */
function browserTimers(): HanziJumpTimers {
  const hasFrame = typeof requestAnimationFrame === 'function';
  return {
    requestFrame: (callback) =>
      hasFrame ? requestAnimationFrame(callback) : window.setTimeout(callback, 0),
    cancelFrame: (handle) => {
      if (hasFrame) cancelAnimationFrame(handle);
      else window.clearTimeout(handle);
    },
    setTimeout: (callback, ms) => window.setTimeout(callback, ms),
    clearTimeout: (handle) => window.clearTimeout(handle),
  };
}

/** `Modal` 打开期间 `body` 是 `overflow: hidden`（那时滚不动，也不该滚）。 */
function pageScrollUnlocked(): boolean {
  if (typeof document === 'undefined') return true;
  return document.body.style.overflow !== 'hidden';
}

/**
 * 字卡登记表：**key → 那张卡的 DOM**。
 *
 * 单独抽出来是为了能**在没有 DOM 的测试里**断言"同名两行 / 三行各登记到各的键"：
 * `useHanziJump` 只是 React 的那一层（state + 定时器），登记/解析本身是纯数据结构。
 *
 * ⚠️ 同一个 key 必须拿到**同一个 ref 回调**：React 在每次渲染都会调用 ref
 *    （先 `null` 再元素），回调身份一变就会"解绑再绑"，多卡时会互相踩。
 */
export interface HanziJumpRegistry {
  register(key: string): (element: HTMLElement | null) => void;
  /** 这个 key 登记的那张卡（还没渲染出来 ⇒ `undefined`）。 */
  resolve(key: string): HTMLElement | undefined;
  /** 清掉所有登记（卸载 / 换筛选）。 */
  clear(): void;
}

export function createHanziJumpRegistry(): HanziJumpRegistry {
  const elements = new Map<string, HTMLElement>();
  const refCallbacks = new Map<string, (element: HTMLElement | null) => void>();
  return {
    register(key) {
      const cached = refCallbacks.get(key);
      if (cached !== undefined) return cached;
      const callback = (element: HTMLElement | null) => {
        if (element === null) {
          elements.delete(key);
          return;
        }
        elements.set(key, element);
      };
      refCallbacks.set(key, callback);
      return callback;
    },
    resolve(key) {
      return elements.get(key);
    },
    clear() {
      elements.clear();
      refCallbacks.clear();
    },
  };
}

/**
 * 字卡跳转的 React 入口。**两处（导入预览 / 浏览与编辑）各实例化一次，实现只有这一份。**
 *
 * 用法（见 `ImportListingPreview` / `DictationWorkspace`）：
 *
 * ```tsx
 * const jump = useHanziJump();
 * <HanziCard cardRef={jump.register(key)} highlighted={jump.activeKey === key} />
 * // 点浮窗里的"还出现在"：
 * closeModal();          // 先关窗（body 解锁）
 * jump.jumpTo(key);      // 滚过去 + 高亮 2 秒；**不打开任何浮窗**
 * ```
 */
export function useHanziJump(): HanziJumpApi {
  const [activeKey, setActiveKey] = useState<string | null>(null);

  /** 已登记的字卡 DOM（纯数据结构，见 `createHanziJumpRegistry()`）。 */
  const registryRef = useRef<HanziJumpRegistry | null>(null);
  if (registryRef.current === null) registryRef.current = createHanziJumpRegistry();

  const timersRef = useRef<HanziJumpTimers | null>(null);
  const runRef = useRef<HanziJumpRun | null>(null);

  const timersOf = useCallback((): HanziJumpTimers => {
    if (timersRef.current === null) timersRef.current = browserTimers();
    return timersRef.current;
  }, []);

  const register = useCallback((key: string) => {
    return registryRef.current!.register(key);
  }, []);

  const cancel = useCallback(() => {
    runRef.current?.cancel();
    runRef.current = null;
    setActiveKey(null);
  }, []);

  const jumpTo = useCallback(
    (key: string) => {
      runRef.current?.cancel();
      runRef.current = null;
      setActiveKey(key);

      const timers = timersOf();
      let cancelled = false;
      let retryFrame: number | null = null;
      let activeRun: HanziJumpRun | null = null;

      const stop = () => {
        cancelled = true;
        if (retryFrame !== null) timers.cancelFrame(retryFrame);
        retryFrame = null;
        activeRun?.cancel();
        activeRun = null;
      };

      /** 记下这一次跳转，下一次跳转 / 取消 / 卸载时能把它停掉。 */
      runRef.current = { cancel: stop };

      const attempt = (frame: number) => {
        if (cancelled) return;
        const element = registryRef.current!.resolve(key);
        if (element === undefined) {
          if (frame >= HANZI_JUMP_MAX_WAIT_FRAMES) {
            /**
             * 目标始终没渲染出来：没得滚就**什么都不做**（高亮也清掉）。
             * ⛔ 不要在这里"顺手打开浮窗"替用户完成下一步 —— 用户点名不要。
             */
            setActiveKey(null);
            return;
          }
          retryFrame = timers.requestFrame(() => attempt(frame + 1));
          return;
        }
        /**
         * 浮窗刚关、`body` 还锁着时再等几帧；等不到（别的浮窗开着）也照滚 ——
         * `overflow: hidden` 下 `scrollIntoView` 仍可能生效，总比什么都不做好。
         */
        if (!pageScrollUnlocked() && frame < HANZI_JUMP_MAX_LOCK_WAIT_FRAMES) {
          retryFrame = timers.requestFrame(() => attempt(frame + 1));
          return;
        }
        retryFrame = null;
        activeRun = runHanziJump({
          element,
          reduceMotion: prefersReducedMotion(),
          timers,
          onHighlightEnd: () => {
            /** 只清"还亮着的那一个"，别把之后的新跳转一起清掉。 */
            setActiveKey((current) => (current === key ? null : current));
          },
        });
      };

      retryFrame = timers.requestFrame(() => attempt(0));
    },
    [timersOf],
  );

  /** 卸载时把定时器停掉：否则组件没了还会 `setActiveKey`。 */
  useEffect(
    () => () => {
      runRef.current?.cancel();
      runRef.current = null;
    },
    [],
  );

  return { register, jumpTo, cancel, activeKey };
}
